// ─────────────────────────────────────────────────────────────────
// Control Systems — PID Control & Tuning
// ─────────────────────────────────────────────────────────────────

const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0 ? S.tdE : S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0 ? S.tdE : S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Arr({ id, color }) {
  return (
    <marker id={id} markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
      <polygon points="0 0,7 3,0 6" fill={color}/>
    </marker>
  );
}

// ─────────────────────────────────────────────────────────────────
// Shared Euler ODE solver — simulate PID closed-loop on first-order plant
// Plant: G(s) = 1/((s+1)(0.5s+1))  (two-pole, no zeros)
// ─────────────────────────────────────────────────────────────────
function simulatePID(Kp, Ki, Kd, tMax, dt, ref, Nd) {
  // State: x1 = first pole output, x2 = second pole output
  // ẋ1 = -x1 + u_clamped
  // ẋ2 = -2x2 + 2x1   (pole at -2)
  // y   = x2
  const N = Math.round(tMax / dt);
  const pts = [];
  let x1 = 0, x2 = 0;
  let integral = 0;
  let prevErr  = 0;
  let dFilt    = 0; // filtered derivative state
  const filterAlpha = Nd > 0 ? (Nd * dt) / (1 + Nd * dt) : 1; // derivative filter

  for (let i = 0; i <= N; i++) {
    const t   = i * dt;
    const r   = typeof ref === 'function' ? ref(t) : ref;
    const y   = x2;
    const err = r - y;

    integral += err * dt;
    const rawDeriv = (err - prevErr) / dt;
    dFilt = dFilt + filterAlpha * (rawDeriv - dFilt);

    const u = Kp * err + Ki * integral + Kd * dFilt;
    const uClamped = Math.max(-10, Math.min(10, u));

    // Anti-windup: don't integrate when saturated
    if (uClamped === u) {
      // not saturated — already added above
    } else {
      integral -= err * dt; // undo the integration when clamped
    }

    pts.push({ t, y, u: uClamped, err });
    prevErr = err;

    // Euler step: plant = 1/((s+1)(0.5s+1))
    const dx1 = -x1 + uClamped;
    const dx2 = -2 * x2 + 2 * x1;
    x1 += dx1 * dt;
    x2 += dx2 * dt;
  }
  return pts;
}

// Simulate WITHOUT anti-windup (for comparison)
function simulatePID_noAW(Kp, Ki, Kd, tMax, dt, ref) {
  const N = Math.round(tMax / dt);
  const pts = [];
  let x1 = 0, x2 = 0;
  let integral = 0;
  let prevErr  = 0;

  for (let i = 0; i <= N; i++) {
    const t   = i * dt;
    const r   = typeof ref === 'function' ? ref(t) : ref;
    const y   = x2;
    const err = r - y;
    integral += err * dt;
    const rawDeriv = (err - prevErr) / dt;
    const u = Kp * err + Ki * integral + Kd * rawDeriv;
    const uClamped = Math.max(-10, Math.min(10, u));
    pts.push({ t, y, u: uClamped, err });
    prevErr = err;
    const dx1 = -x1 + uClamped;
    const dx2 = -2 * x2 + 2 * x1;
    x1 += dx1 * dt;
    x2 += dx2 * dt;
  }
  return pts;
}

// Path builder helper
function mkPath(pts, sx, sy, key) {
  return pts.map((p, i) => `${i===0?'M':'L'}${sx(p.t).toFixed(1)},${sy(p[key]).toFixed(1)}`).join(' ');
}

// ─────────────────────────────────────────────────────────────────
// Diagram 1 — PID Block Diagram & Structure
// ─────────────────────────────────────────────────────────────────
function DiagramPIDStructure() {
  const W = 900, H = 390;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>PID Controller — Parallel Structure, Time Domain &amp; Laplace Domain</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="ps-blk" color="#555"/>
          <Arr id="ps-red" color="#c0392b"/>
          <Arr id="ps-grn" color="#27ae60"/>
          <Arr id="ps-blu" color="#2E75B6"/>
          <Arr id="ps-orn" color="#e07b00"/>
        </defs>

        {/* ── LEFT: Full closed-loop block diagram ── */}
        <rect x={10} y={10} width={520} height={230} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={270} y={28} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">PID Closed-Loop Block Diagram</text>

        {/* R(s) */}
        <text x={16} y={130} fontSize={11} fill="#c0392b" fontWeight="700">R(s)</text>
        <line x1={40} y1={126} x2={76} y2={126} stroke="#c0392b" strokeWidth="2" markerEnd="url(#ps-red)"/>

        {/* Summing junction */}
        <circle cx={94} cy={126} r={17} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <text x={94}  y={131} textAnchor="middle" fontSize={15} fill="#c0392b" fontWeight="700">Σ</text>
        <text x={78}  y={122} fontSize={10} fill="#27ae60" fontWeight="700">+</text>
        <text x={90}  y={143} fontSize={10} fill="#c0392b" fontWeight="700">−</text>
        <line x1={111} y1={126} x2={142} y2={126} stroke="#555" strokeWidth="2" markerEnd="url(#ps-blk)"/>
        <text x={116}  y={119} fontSize={9.5} fill="#555">E(s)</text>

        {/* Pick-off to three branches */}
        <circle cx={155} cy={126} r={4} fill="#555"/>

        {/* P branch — middle */}
        <line x1={155} y1={126} x2={195} y2={126} stroke="#555" strokeWidth="1.8" markerEnd="url(#ps-blk)"/>
        <rect x={195} y={108} width={76} height={36} rx={4} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.8"/>
        <text x={233} y={124} textAnchor="middle" fontSize={13} fill="#c0392b" fontWeight="700">Kp</text>
        <text x={233} y={137} textAnchor="middle" fontSize={9}  fill="#7B0D0D">Proportional</text>
        <line x1={271} y1={126} x2={336} y2={126} stroke="#c0392b" strokeWidth="1.8" markerEnd="url(#ps-red)"/>

        {/* I branch — top */}
        <line x1={155} y1={126} x2={155} y2={72} stroke="#555" strokeWidth="1.5"/>
        <line x1={155} y1={72}  x2={195} y2={72} stroke="#555" strokeWidth="1.5" markerEnd="url(#ps-blk)"/>
        <rect x={195} y={54} width={76} height={36} rx={4} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.8"/>
        <text x={233} y={70} textAnchor="middle" fontSize={12} fill="#2E75B6" fontWeight="700">Ki/s</text>
        <text x={233} y={83} textAnchor="middle" fontSize={9}  fill="#2E75B6">Integral</text>
        <line x1={271} y1={72} x2={336} y2={72} stroke="#2E75B6" strokeWidth="1.8"/>
        <line x1={336} y1={72} x2={336} y2={120} stroke="#2E75B6" strokeWidth="1.8" markerEnd="url(#ps-blu)"/>

        {/* D branch — bottom */}
        <line x1={155} y1={126} x2={155} y2={182} stroke="#555" strokeWidth="1.5"/>
        <line x1={155} y1={182} x2={195} y2={182} stroke="#555" strokeWidth="1.5" markerEnd="url(#ps-blk)"/>
        <rect x={195} y={164} width={76} height={36} rx={4} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.8"/>
        <text x={233} y={180} textAnchor="middle" fontSize={12} fill="#27ae60" fontWeight="700">Kd·s</text>
        <text x={233} y={193} textAnchor="middle" fontSize={9}  fill="#27ae60">Derivative</text>
        <line x1={271} y1={182} x2={336} y2={182} stroke="#27ae60" strokeWidth="1.8"/>
        <line x1={336} y1={182} x2={336} y2={132} stroke="#27ae60" strokeWidth="1.8" markerEnd="url(#ps-grn)"/>

        {/* Output summing junction */}
        <circle cx={354} cy={126} r={17} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <text x={354} y={131} textAnchor="middle" fontSize={15} fill="#c0392b" fontWeight="700">Σ</text>
        <text x={338} y={122} fontSize={10} fill="#27ae60" fontWeight="700">+</text>
        <line x1={371} y1={126} x2={406} y2={126} stroke="#555" strokeWidth="2" markerEnd="url(#ps-blk)"/>
        <text x={378} y={119} fontSize={9.5} fill="#555">U(s)</text>

        {/* Plant */}
        <rect x={406} y={108} width={76} height={36} rx={4} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1.8"/>
        <text x={444} y={124} textAnchor="middle" fontSize={12} fill="#9b59b6" fontWeight="700">G(s)</text>
        <text x={444} y={137} textAnchor="middle" fontSize={9}  fill="#9b59b6">Plant</text>
        <line x1={482} y1={126} x2={524} y2={126} stroke="#555" strokeWidth="2" markerEnd="url(#ps-blk)"/>
        <circle cx={504} cy={126} r={4} fill="#555"/>
        <text x={510} y={119} fontSize={10} fill="#27ae60" fontWeight="700">Y(s)</text>

        {/* Feedback */}
        <line x1={504} y1={130} x2={504} y2={200} stroke="#555" strokeWidth="1.5"/>
        <line x1={504} y1={200} x2={94}  y2={200} stroke="#555" strokeWidth="1.5"/>
        <line x1={94}  y1={200} x2={94}  y2={143} stroke="#555" strokeWidth="1.5" markerEnd="url(#ps-blk)"/>

        {/* TF formula */}
        <rect x={18} y={212} width={502} height={22} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={269} y={227} textAnchor="middle" fontSize={10.5} fill="#7B0D0D" fontFamily="'Courier New',monospace">
          C(s) = Kp + Ki/s + Kd·s = (Kd·s² + Kp·s + Ki) / s
        </text>

        {/* ── RIGHT: Time domain + properties ── */}
        <rect x={542} y={10} width={348} height={230} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={716} y={28} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Time-Domain PID Law</text>

        {/* Time domain formula box */}
        <rect x={550} y={36} width={332} height={46} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={716} y={55} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontFamily="'Courier New',monospace" fontWeight="700">u(t) = Kp·e(t) + Ki∫e(τ)dτ + Kd·de/dt</text>
        <text x={716} y={73} textAnchor="middle" fontSize={9.5} fill="#555">e(t) = r(t) − y(t)  (error signal)</text>

        {/* Three terms explained */}
        {[
          { y:88,  fill:'#FEF0F0', bc:'#c0392b', sym:'Kp', name:'Proportional',
            lines:['Acts on current error magnitude','Reduces error; can leave offset','Large Kp → fast response, more OS'] },
          { y:142, fill:'#eaf3ff', bc:'#2E75B6', sym:'Ki', name:'Integral',
            lines:['Accumulates past error × time','Eliminates steady-state offset','Large Ki → faster, risk of windup'] },
          { y:196, fill:'#f0fff4', bc:'#27ae60', sym:'Kd', name:'Derivative',
            lines:['Reacts to rate of error change','Damps oscillation, predicts future','Large Kd → noise amplification'] },
        ].map(({ y, fill, bc, sym, name, lines }) => (
          <g key={y}>
            <rect x={550} y={y} width={332} height={50} rx={3} fill={fill} stroke={bc} strokeWidth="1.2"/>
            <rect x={550} y={y} width={48} height={50} rx={3} fill={bc} opacity="0.18"/>
            <text x={574} y={y+20} textAnchor="middle" fontSize={14} fill={bc} fontWeight="700">{sym}</text>
            <text x={574} y={y+36} textAnchor="middle" fontSize={9}  fill={bc}>{name}</text>
            {lines.map((l, j) => (
              <text key={j} x={606} y={y+16+j*14} fontSize={9} fill="#333">{l}</text>
            ))}
          </g>
        ))}

        {/* ── BOTTOM: Parallel / Standard / Series forms ── */}
        <rect x={10} y={250} width={880} height={130} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={450} y={268} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Three Equivalent PID Forms</text>

        {[
          {
            x:18, color:'#c0392b', bg:'#FEF0F0', title:'Parallel (Ideal)',
            f1:'C(s) = Kp + Ki/s + Kd·s',
            f2:'Gains: Kp, Ki, Kd independent',
            note:'Most common in textbooks and digital implementations',
          },
          {
            x:308, color:'#2E75B6', bg:'#eaf3ff', title:'Standard (ISA)',
            f1:'C(s) = Kp[1 + 1/(Ti·s) + Td·s]',
            f2:'Ti = Kp/Ki  (integral time)   Td = Kd/Kp',
            note:'Ti = reset time; Td = derivative time. Used in industry.',
          },
          {
            x:598, color:'#27ae60', bg:'#f0fff4', title:'Series (Interacting)',
            f1:'C(s) = Kp(1 + 1/Ti·s)(1 + Td·s)',
            f2:'Poles/zeros different from parallel form',
            note:'Older pneumatic/analogue controllers; easier to tune manually.',
          },
        ].map(({ x, color, bg, title, f1, f2, note }) => (
          <g key={x}>
            <rect x={x+2} y={276} width={282} height={98} rx={3} fill={bg} stroke={color} strokeWidth="1.2"/>
            <text x={x+10} y={292} fontSize={10.5} fill={color} fontWeight="700">{title}</text>
            <text x={x+10} y={308} fontSize={10}   fill="#333" fontFamily="'Courier New',monospace">{f1}</text>
            <text x={x+10} y={323} fontSize={9.5}  fill="#555" fontFamily="'Courier New',monospace">{f2}</text>
            <line x1={x+2} y1={330} x2={x+282} y2={330} stroke={color} strokeWidth="0.5" opacity="0.4"/>
            <text x={x+10} y={344} fontSize={8.5} fill="#555" fontStyle="italic">{note}</text>
            <text x={x+10} y={367} fontSize={8.5} fill="#555" fontStyle="italic">
              {x===18?'Kp=1, Ki=2, Kd=0.5 example':x===308?'Ti=Kp/Ki, Td=Kd/Kp':'Parameters interact — tune sequentially'}
            </text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The PID controller computes three parallel terms from the error signal e(t)=r(t)−y(t): a proportional term (present error), an integral term (accumulated past error), and a derivative term (predicted future error). Together, C(s)=(Kd·s²+Kp·s+Ki)/s, they add a zero pair and an integrator to the open-loop transfer function. Three equivalent forms exist — parallel, standard (ISA), and series — each with different parameter relationships.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 2 — Individual P, I, D Term Effects (computed)
// ─────────────────────────────────────────────────────────────────
function DiagramIndividualEffects() {
  const W = 900, H = 370;
  const dt = 0.02, tMax = 12;

  // P only (no I, no D) — varying Kp
  const pOnly = [
    { Kp:0.5, Ki:0, Kd:0, color:'#e07b00',  label:'Kp=0.5' },
    { Kp:2.0, Ki:0, Kd:0, color:'#c0392b',  label:'Kp=2.0' },
    { Kp:6.0, Ki:0, Kd:0, color:'#9b59b6',  label:'Kp=6.0' },
  ];
  const pPaths = pOnly.map(({ Kp, Ki, Kd, color, label }) => {
    const pts = simulatePID(Kp, Ki, Kd, tMax, dt, 1, 20);
    return { pts, color, label, Kp };
  });

  // I effect: fix Kp=2, vary Ki
  const iVary = [
    { Kp:2, Ki:0,   Kd:0, color:'#c0392b', label:'Ki=0' },
    { Kp:2, Ki:0.5, Kd:0, color:'#e07b00', label:'Ki=0.5' },
    { Kp:2, Ki:2.0, Kd:0, color:'#2E75B6', label:'Ki=2.0' },
    { Kp:2, Ki:5.0, Kd:0, color:'#27ae60', label:'Ki=5.0' },
  ];
  const iPaths = iVary.map(({ Kp, Ki, Kd, color, label }) => {
    const pts = simulatePID(Kp, Ki, Kd, tMax, dt, 1, 20);
    return { pts, color, label };
  });

  // D effect: fix Kp=4, Ki=1, vary Kd
  const dVary = [
    { Kp:4, Ki:1, Kd:0,    color:'#c0392b', label:'Kd=0' },
    { Kp:4, Ki:1, Kd:0.3,  color:'#e07b00', label:'Kd=0.3' },
    { Kp:4, Ki:1, Kd:1.0,  color:'#2E75B6', label:'Kd=1.0' },
    { Kp:4, Ki:1, Kd:2.5,  color:'#27ae60', label:'Kd=2.5' },
  ];
  const dPaths = dVary.map(({ Kp, Ki, Kd, color, label }) => {
    const pts = simulatePID(Kp, Ki, Kd, tMax, dt, 1, 20);
    return { pts, color, label };
  });

  const panels = [
    { title:'P Only — Varying Kp',   subtitle:'Ki=0, Kd=0  |  Higher Kp: faster but more OS, residual offset', paths:pPaths,  ox:16,  note:'Proportional gain reduces error but cannot eliminate it (Type 0 plant → finite e_ss)' },
    { title:'PI — Varying Ki',        subtitle:'Kp=2, Kd=0  |  Higher Ki: eliminates offset, faster but more OS', paths:iPaths, ox:314, note:'Integral action eliminates steady-state error — all responses reach y=1 eventually' },
    { title:'PID — Varying Kd',       subtitle:'Kp=4, Ki=1  |  Higher Kd: damps overshoot, improves settling', paths:dPaths, ox:612, note:'Derivative action damps the response — reduces overshoot and settling time' },
  ];

  const PW = 280, PH = 230;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Individual P, I, D Term Effects — Computed Step Responses</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="ie-blk" color="#aaa"/>
          {['c0','c1','c2'].map(id => (
            <clipPath key={id} id={`ie-${id}`}>
              <rect x={id==='c0'?48:id==='c1'?346:644} y={28} width={PW-36} height={PH-24}/>
            </clipPath>
          ))}
        </defs>

        {panels.map((panel, pi) => {
          const clipId = `url(#ie-c${pi})`;
          const sx = t  => panel.ox + 32 + (t/tMax)*(PW-44);
          const sy = yv => 30 + PH - 22 - Math.max(Math.min(yv,2.1),-0.1) * (PH-40);

          return (
            <g key={pi}>
              <rect x={panel.ox} y={20} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
              <text x={panel.ox+PW/2} y={14} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">{panel.title}</text>

              {/* Steady-state line */}
              <line x1={panel.ox+30} y1={sy(1)} x2={panel.ox+PW-10} y2={sy(1)} stroke="#27ae60" strokeWidth="1.2" strokeDasharray="6,4" opacity="0.7"/>
              <text x={panel.ox+PW-6} y={sy(1)-3} fontSize={8} fill="#27ae60">1</text>

              {/* Zero line */}
              <line x1={panel.ox+30} y1={sy(0)} x2={panel.ox+PW-10} y2={sy(0)} stroke="#ddd" strokeWidth="1"/>

              {/* Grid */}
              {[3,6,9,12].map(tv => (
                <line key={tv} x1={sx(tv)} y1={28} x2={sx(tv)} y2={28+PH-22} stroke="#f8f8f8" strokeWidth="1"/>
              ))}

              {/* Axes */}
              <line x1={panel.ox+30} y1={28+PH-20} x2={panel.ox+PW-6} y2={28+PH-20} stroke="#bbb" strokeWidth="1.2" markerEnd="url(#ie-blk)"/>
              <line x1={panel.ox+30} y1={28+PH-20} x2={panel.ox+30} y2={26} stroke="#bbb" strokeWidth="1.2" markerEnd="url(#ie-blk)"/>
              <text x={panel.ox+PW-2} y={28+PH-16} fontSize={7.5} fill="#aaa">t</text>

              {/* Curves */}
              {panel.paths.map(({ pts, color, label }, i) => {
                const d = mkPath(pts, sx, sy, 'y');
                return <path key={i} d={d} fill="none" stroke={color} strokeWidth="2" clipPath={clipId}/>;
              })}

              {/* Legend */}
              {panel.paths.map(({ color, label }, i) => (
                <g key={i}>
                  <line x1={panel.ox+6} y1={256+i*14} x2={panel.ox+22} y2={256+i*14} stroke={color} strokeWidth="2"/>
                  <text x={panel.ox+26} y={260+i*14} fontSize={8.5} fill={color}>{label}</text>
                </g>
              ))}

              {/* Note */}
              <rect x={panel.ox+2} y={322} width={PW-4} height={38} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="0.8"/>
              <text x={panel.ox+PW/2} y={334} textAnchor="middle" fontSize={8}   fill="#7B0D0D" fontWeight="700">{panel.subtitle}</text>
              <text x={panel.ox+PW/2} y={347} textAnchor="middle" fontSize={7.5} fill="#555">{panel.note}</text>
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>Left: P-only control cannot eliminate steady-state error (residual offset visible at higher Kp). Centre: adding integral (Ki) drives all responses to y=1 — zero steady-state error — with higher Ki trading faster correction for more overshoot. Right: adding derivative (Kd) progressively damps the overshoot and reduces settling time. Well-tuned PID balances all three effects simultaneously.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 3 — Ziegler-Nichols Step (Open-Loop) Method
// ─────────────────────────────────────────────────────────────────
function DiagramZNStep() {
  const W = 900, H = 370;

  // Simulate open-loop step response of a typical process
  // Plant: G(s) = 2·e^{-Ls} / (Ts+1)  approximated by series of poles
  // Use: G(s) = 2/((s+1)(0.5s+1)(0.25s+1))  → approximate S-curve
  const dt   = 0.02;
  const tMax = 14;
  const N    = Math.round(tMax / dt);

  const olPts = [];
  let x1 = 0, x2 = 0, x3 = 0;
  const uStep = 1; // unit step at t=0.5

  for (let i = 0; i <= N; i++) {
    const t = i * dt;
    const u = t >= 0.5 ? uStep : 0;
    olPts.push({ t, y: x3 * 2 }); // gain = 2
    const dx1 = -x1 + u;
    const dx2 = -2*x2 + 2*x1;
    const dx3 = -4*x3 + 4*x2;
    x1 += dx1*dt; x2 += dx2*dt; x3 += dx3*dt;
  }

  // S-curve parameters: find inflection point and tangent
  // Find max slope point
  let maxSlope = 0, maxIdx = 0;
  for (let i = 1; i < olPts.length; i++) {
    const slope = (olPts[i].y - olPts[i-1].y) / dt;
    if (slope > maxSlope) { maxSlope = slope; maxIdx = i; }
  }
  const tInfl  = olPts[maxIdx].t;
  const yInfl  = olPts[maxIdx].y;
  const K_proc = olPts[olPts.length-1].y; // process gain K ≈ 2
  // Tangent line: y = yInfl + maxSlope*(t - tInfl)
  // L = t where tangent crosses y=0:  t = tInfl - yInfl/maxSlope
  const L = tInfl - yInfl / maxSlope;
  // T = time where tangent crosses y=K: (K - yInfl)/maxSlope + tInfl
  const Tconst = (K_proc - yInfl) / maxSlope + tInfl - L;

  const PW = 430, PH = 240;
  const ox = 20, oy = 30;
  const sx = t  => ox + 34 + (t/tMax)*(PW-50);
  const sy = yv => oy + PH - 22 - (yv/2.2)*(PH-36);

  const olPath = olPts.map(({ t, y }, i) =>
    `${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');

  // Tangent line endpoints
  const tTan1 = L - 0.3;
  const tTan2 = L + Tconst + 0.5;

  // ZN table data
  const znRows = [
    { ctrl:'P',   Kp:'T/(K·L)',   Ti:'—',   Td:'—' },
    { ctrl:'PI',  Kp:'0.9T/(K·L)',Ti:'L/0.3',Td:'—' },
    { ctrl:'PID', Kp:'1.2T/(K·L)',Ti:'2L',   Td:'0.5L' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Ziegler-Nichols Open-Loop (Process Reaction / S-Curve) Tuning Method</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="zns-blk" color="#aaa"/>
          <Arr id="zns-orn" color="#e07b00"/>
          <clipPath id="zns-clip"><rect x={54} y={32} width={PW-46} height={PH-20}/></clipPath>
        </defs>

        {/* Plot */}
        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox+PW/2} y={oy-8} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Open-Loop Step Response — Identifying L and T</text>

        {/* Grid */}
        {[2,4,6,8,10,12,14].map(tv => (
          <line key={tv} x1={sx(tv)} y1={oy+8} x2={sx(tv)} y2={oy+PH-20} stroke="#f8f8f8" strokeWidth="1"/>
        ))}
        {[0.5,1.0,1.5,2.0].map(yv => (
          <line key={yv} x1={ox+32} y1={sy(yv)} x2={ox+PW-16} y2={sy(yv)} stroke="#f8f8f8" strokeWidth="1"/>
        ))}

        {/* Final value line */}
        <line x1={ox+32} y1={sy(K_proc)} x2={ox+PW-16} y2={sy(K_proc)} stroke="#27ae60" strokeWidth="1.2" strokeDasharray="5,4" opacity="0.7"/>
        <text x={ox+36} y={sy(K_proc)-4} fontSize={8.5} fill="#27ae60">K·u = {K_proc.toFixed(1)}</text>

        {/* Axes */}
        <line x1={ox+32} y1={oy+PH-20} x2={ox+PW-10} y2={oy+PH-20} stroke="#aaa" strokeWidth="1.3" markerEnd="url(#zns-blk)"/>
        <line x1={ox+32} y1={oy+PH-20} x2={ox+32}    y2={oy+6}     stroke="#aaa" strokeWidth="1.3" markerEnd="url(#zns-blk)"/>
        <text x={ox+PW-6} y={oy+PH-16} fontSize={8.5} fill="#aaa">t</text>
        <text x={ox+36}   y={oy+6}      fontSize={8.5} fill="#aaa">y</text>

        {/* S-curve */}
        <path d={olPath} fill="none" stroke="#c0392b" strokeWidth="2.5" clipPath="url(#zns-clip)"/>

        {/* Inflection tangent */}
        <line x1={sx(tTan1)} y1={sy(yInfl + maxSlope*(tTan1-tInfl))}
              x2={sx(tTan2)} y2={sy(yInfl + maxSlope*(tTan2-tInfl))}
              stroke="#e07b00" strokeWidth="2" strokeDasharray="8,4" clipPath="url(#zns-clip)"/>
        <text x={sx(tInfl)-20} y={sy(yInfl)-10} fontSize={9} fill="#c0392b" fontWeight="700">Inflection</text>
        <circle cx={sx(tInfl)} cy={sy(yInfl)} r={5} fill="#e07b00" stroke="#fff" strokeWidth="2"/>

        {/* L annotation */}
        <line x1={sx(0.5)} y1={oy+PH-20} x2={sx(0.5)} y2={oy+PH-6} stroke="#2E75B6" strokeWidth="1.5"/>
        <line x1={sx(L)}   y1={oy+PH-20} x2={sx(L)}   y2={oy+PH-6} stroke="#2E75B6" strokeWidth="1.5"/>
        <line x1={sx(0.5)} y1={oy+PH-12} x2={sx(L)}   y2={oy+PH-12} stroke="#2E75B6" strokeWidth="2" markerStart="url(#zns-orn)" markerEnd="url(#zns-orn)"/>
        <text x={(sx(0.5)+sx(L))/2} y={oy+PH+2} textAnchor="middle" fontSize={10} fill="#2E75B6" fontWeight="700">L={L.toFixed(2)}s</text>

        {/* T annotation */}
        <line x1={sx(L)} y1={oy+PH-24} x2={sx(L+Tconst)} y2={oy+PH-24} stroke="#9b59b6" strokeWidth="2" markerStart="url(#zns-orn)" markerEnd="url(#zns-orn)"/>
        <text x={(sx(L)+sx(L+Tconst))/2} y={oy+PH-28} textAnchor="middle" fontSize={10} fill="#9b59b6" fontWeight="700">T={Tconst.toFixed(2)}s</text>

        {/* Drop lines */}
        <line x1={sx(L)} y1={sy(0)} x2={sx(L)} y2={sy(K_proc)} stroke="#2E75B6" strokeWidth="1" strokeDasharray="3,2" opacity="0.6"/>
        <line x1={sx(L+Tconst)} y1={sy(0)} x2={sx(L+Tconst)} y2={sy(K_proc)} stroke="#9b59b6" strokeWidth="1" strokeDasharray="3,2" opacity="0.6"/>

        {/* Step annotation */}
        <text x={sx(0.5)+4} y={oy+PH-36} fontSize={8.5} fill="#555">u=1 applied</text>

        {/* ── RIGHT: ZN table + procedure ── */}
        <rect x={466} y={oy} width={424} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={678} y={oy+18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">ZN Open-Loop Tuning Formulas</text>

        <text x={474} y={oy+36} fontSize={10} fill="#7B0D0D" fontWeight="700">Procedure:</text>
        {['1. Apply unit step to plant (open loop, R=0)',
          '2. Record output response curve',
          '3. Draw tangent at inflection point',
          '4. Read off: L = dead time, T = time constant',
          '5. Compute K = ΔOutput/ΔInput (process gain)',
          '6. Look up Kp, Ti, Td from table below'].map((line, i) => (
          <text key={i} x={474} y={oy+50+i*16} fontSize={9.5} fill="#333">{line}</text>
        ))}

        {/* Table header */}
        <rect x={474} y={oy+152} width={408} height={22} rx={2} fill="#c0392b"/>
        {['Controller','Kp','Ti (=Kp/Ki)','Td (=Kd/Kp)'].map((h, i) => (
          <text key={i} x={[482,578,652,752][i]} y={oy+167} fontSize={10} fill="#fff" fontWeight="700">{h}</text>
        ))}
        {znRows.map(({ ctrl, Kp, Ti, Td }, i) => (
          <g key={i}>
            <rect x={474} y={oy+174+i*22} width={408} height={22} fill={i%2===0?'#fff5f5':'#fff'}/>
            <text x={482} y={oy+189+i*22} fontSize={10} fill="#7B0D0D" fontWeight="700">{ctrl}</text>
            <text x={578} y={oy+189+i*22} fontSize={9.5} fill="#333" fontFamily="'Courier New',monospace">{Kp}</text>
            <text x={652} y={oy+189+i*22} fontSize={9.5} fill="#333" fontFamily="'Courier New',monospace">{Ti}</text>
            <text x={752} y={oy+189+i*22} fontSize={9.5} fill="#333" fontFamily="'Courier New',monospace">{Td}</text>
          </g>
        ))}

        <rect x={474} y={oy+240} width={408} height={0} rx={2}/>

        {/* Warning */}
        <rect x={474} y={oy+243} width={408} height={28} rx={3} fill="#fff5e6" stroke="#e07b00" strokeWidth="1"/>
        <text x={678} y={oy+255} textAnchor="middle" fontSize={9.5} fill="#e07b00" fontWeight="700">⚠ ZN open-loop gives aggressive initial tuning (≈25% OS)</text>
        <text x={678} y={oy+267} textAnchor="middle" fontSize={9}   fill="#555">Reduce Kp by ×0.5–0.7 for more conservative performance</text>
      </svg>

      {/* Bottom: step annotations */}
      <div style={{ ...S.dCaption }}>
        The open-loop step (S-curve) method applies a step to the open-loop plant and fits a first-order-plus-dead-time (FOPDT) model: G(s) ≈ K·e^(-Ls)/(Ts+1). Dead time L and time constant T are read from the tangent line at the inflection point. The ZN formulas then give initial PID gains that typically produce ~25% overshoot — a starting point for further refinement.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 4 — Ziegler-Nichols Ultimate Gain (Closed-Loop) Method
// ─────────────────────────────────────────────────────────────────
function DiagramZNUltimate() {
  const W = 900, H = 380;
  const dt = 0.01, tMax = 20;

  // Simulate closed-loop P-only to find ultimate gain
  // Plant: G(s) = 1/((s+1)(0.5s+1)(0.25s+1))
  // Ultimate gain Ku: where sustained oscillation occurs
  // For this plant Ku ≈ 9.8 (found analytically: phase = -180° at ω = sqrt(11)/sqrt(0.5) roughly)
  // We'll simulate K=2 (stable), K=Ku≈9.8 (marginal), K=14 (unstable)

  const simPlantP = (Kp, tMax, dt) => {
    const N = Math.round(tMax / dt);
    const pts = [];
    let x1=0, x2=0, x3=0;
    for (let i=0; i<=N; i++) {
      const t = i*dt;
      const r = 1;
      const y = x3;
      const e = r - y;
      const u = Math.max(-20, Math.min(20, Kp*e));
      pts.push({ t, y });
      const dx1 = -x1 + u;
      const dx2 = -2*x2 + 2*x1;
      const dx3 = -4*x3 + 4*x2;
      x1+=dx1*dt; x2+=dx2*dt; x3+=dx3*dt;
    }
    return pts;
  };

  const Ku = 10.0; // approximate ultimate gain
  const stablePts  = simPlantP(3,   tMax, dt);
  const marginalPts= simPlantP(Ku,  tMax, dt);
  const unstablePts= simPlantP(15,  tMax, dt);

  // Find Tu from marginal simulation — period of oscillation
  // Find zero crossings of (y-1) in marginal simulation
  const crossings = [];
  for (let i=1; i<marginalPts.length; i++) {
    if ((marginalPts[i-1].y - 1)*(marginalPts[i].y - 1) < 0 && marginalPts[i].t > 5) {
      crossings.push(marginalPts[i].t);
    }
  }
  const Tu = crossings.length >= 2 ? 2 * (crossings[1] - crossings[0]) : 2.8;

  const PW = 430, PH = 250;
  const ox = 20, oy = 28;
  const sx = t  => ox + 34 + (t/tMax)*(PW-50);
  const sy = yv => oy + PH - 22 - Math.max(Math.min(yv,2.5),-0.5) * (PH-40)/2;

  const mkPPts = (pts) => pts.map(({ t, y }, i) =>
    `${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');

  const stPath  = mkPPts(stablePts);
  const margPath= mkPPts(marginalPts);
  const unPath  = mkPPts(unstablePts);

  // ZN Ultimate gain table
  const zuRows = [
    { ctrl:'P',   Kp:'0.5Ku',   Ti:'—',    Td:'—' },
    { ctrl:'PI',  Kp:'0.45Ku',  Ti:'Tu/1.2',Td:'—' },
    { ctrl:'PD',  Kp:'0.8Ku',   Ti:'—',    Td:'Tu/8' },
    { ctrl:'PID', Kp:'0.6Ku',   Ti:'Tu/2',  Td:'Tu/8' },
    { ctrl:'P-I-D (Pessen)', Kp:'0.7Ku', Ti:'Tu/2.5', Td:'0.15Tu' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Ziegler-Nichols Ultimate Gain (Closed-Loop) Tuning Method</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="znu-blk" color="#aaa"/>
          <Arr id="znu-orn" color="#e07b00"/>
          <clipPath id="znu-clip"><rect x={54} y={30} width={PW-46} height={PH-18}/></clipPath>
        </defs>

        {/* Plot */}
        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox+PW/2} y={oy-8} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">P-Only Closed-Loop: Stable → Marginal → Unstable</text>

        {/* Grid & axes */}
        {[5,10,15,20].map(tv => (
          <line key={tv} x1={sx(tv)} y1={oy+8} x2={sx(tv)} y2={oy+PH-20} stroke="#f8f8f8" strokeWidth="1"/>
        ))}
        <line x1={ox+32} y1={sy(1)} x2={ox+PW-16} y2={sy(1)} stroke="#27ae60" strokeWidth="1.2" strokeDasharray="5,4" opacity="0.6"/>
        <text x={ox+36} y={sy(1)-4} fontSize={8.5} fill="#27ae60">r=1</text>
        <line x1={ox+32} y1={oy+PH-20} x2={ox+PW-10} y2={oy+PH-20} stroke="#aaa" strokeWidth="1.3" markerEnd="url(#znu-blk)"/>
        <line x1={ox+32} y1={oy+PH-20} x2={ox+32}    y2={oy+6}     stroke="#aaa" strokeWidth="1.3" markerEnd="url(#znu-blk)"/>
        <text x={ox+PW-6} y={oy+PH-16} fontSize={8} fill="#aaa">t</text>

        {/* Three curves */}
        <path d={stPath}   fill="none" stroke="#27ae60" strokeWidth="2" clipPath="url(#znu-clip)"/>
        <path d={margPath} fill="none" stroke="#e07b00" strokeWidth="2.5" clipPath="url(#znu-clip)"/>
        <path d={unPath}   fill="none" stroke="#c0392b" strokeWidth="2" clipPath="url(#znu-clip)"/>

        {/* Tu annotation on marginal curve — show one period */}
        {crossings.length >= 2 && (
          <g>
            <line x1={sx(crossings[0])} y1={oy+PH-20} x2={sx(crossings[0])} y2={oy+PH-8} stroke="#e07b00" strokeWidth="1.5"/>
            <line x1={sx(crossings[1])} y1={oy+PH-20} x2={sx(crossings[1])} y2={oy+PH-8} stroke="#e07b00" strokeWidth="1.5"/>
            <line x1={sx(crossings[0])} y1={oy+PH-13} x2={sx(crossings[1])} y2={oy+PH-13} stroke="#e07b00" strokeWidth="2" markerStart="url(#znu-orn)" markerEnd="url(#znu-orn)"/>
            <text x={(sx(crossings[0])+sx(crossings[1]))/2} y={oy+PH+2} textAnchor="middle" fontSize={10} fill="#e07b00" fontWeight="700">Tu≈{Tu.toFixed(2)}s</text>
          </g>
        )}

        {/* Labels */}
        <text x={sx(3)}  y={sy(0.78)} fontSize={9} fill="#27ae60" fontWeight="700">Kp=3 (stable)</text>
        <text x={sx(2.5)} y={sy(1.55)} fontSize={9} fill="#e07b00" fontWeight="700">Kp=Ku={Ku} (marginal)</text>
        <text x={sx(1.5)} y={sy(2.35)} fontSize={9} fill="#c0392b" fontWeight="700">Kp=15 (unstable)</text>

        {/* Ku annotation */}
        <rect x={ox+32} y={oy+8} width={110} height={32} rx={3} fill="#fff5e6" stroke="#e07b00" strokeWidth="1"/>
        <text x={ox+38} y={oy+20} fontSize={9}   fill="#e07b00" fontWeight="700">Ku = {Ku} (ultimate gain)</text>
        <text x={ox+38} y={oy+32} fontSize={8.5} fill="#555">Increase Kp until sustained oscillation</text>

        {/* ── RIGHT: Procedure + Table ── */}
        <rect x={466} y={oy} width={424} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={678} y={oy+18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">ZN Ultimate Gain Tuning Formulas</text>

        <text x={474} y={oy+36} fontSize={10} fill="#7B0D0D" fontWeight="700">Procedure:</text>
        {['1. Close the loop with P-only controller (Ki=0, Kd=0)',
          '2. Increase Kp slowly with small step inputs',
          '3. Find Ku: value where output just sustains oscillation',
          '4. Measure Tu: period of those oscillations',
          '5. Apply formulas from table below',
          '6. Fine-tune by simulation or on the real plant'].map((line, i) => (
          <text key={i} x={474} y={oy+50+i*16} fontSize={9.5} fill="#333">{line}</text>
        ))}

        {/* Table */}
        <rect x={474} y={oy+152} width={408} height={22} rx={2} fill="#c0392b"/>
        {['Controller','Kp','Ti','Td'].map((h, i) => (
          <text key={i} x={[482,578,672,752][i]} y={oy+167} fontSize={10} fill="#fff" fontWeight="700">{h}</text>
        ))}
        {zuRows.map(({ ctrl, Kp, Ti, Td }, i) => (
          <g key={i}>
            <rect x={474} y={oy+174+i*20} width={408} height={20} fill={i%2===0?'#fff5f5':'#fff'}/>
            <text x={482} y={oy+188+i*20} fontSize={9.5} fill="#7B0D0D" fontWeight="700">{ctrl}</text>
            <text x={578} y={oy+188+i*20} fontSize={9}   fill="#333" fontFamily="'Courier New',monospace">{Kp}</text>
            <text x={672} y={oy+188+i*20} fontSize={9}   fill="#333" fontFamily="'Courier New',monospace">{Ti}</text>
            <text x={752} y={oy+188+i*20} fontSize={9}   fill="#333" fontFamily="'Courier New',monospace">{Td}</text>
          </g>
        ))}

        <rect x={474} y={oy+278} width={408} height={22} rx={3} fill="#fff5e6" stroke="#e07b00" strokeWidth="1"/>
        <text x={678} y={oy+292} textAnchor="middle" fontSize={9.5} fill="#e07b00" fontWeight="700">Cannot be used if plant cannot sustain oscillation safely</text>

        {/* Bottom procedure steps */}
        <rect x={10} y={oy+PH+12} width={880} height={94} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={450} y={oy+PH+28} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Comparison: ZN Step vs ZN Ultimate Gain</text>

        {[
          { x:18,  color:'#2E75B6', bg:'#eaf3ff', title:'ZN Step (Open-Loop)',
            pros:'Safe — no closed-loop required\nWorks even if plant is open-loop unstable',
            cons:'Needs good S-curve fit\nSensitive to noise in open-loop response' },
          { x:308, color:'#c0392b', bg:'#FEF0F0', title:'ZN Ultimate Gain (Closed-Loop)',
            pros:'More direct — uses closed-loop dynamics\nLess model approximation needed',
            cons:'May not be safe — requires sustained oscillation\nNot usable for unstable or integrating plants' },
          { x:598, color:'#27ae60', bg:'#f0fff4', title:'Both Methods',
            pros:'Quick starting point — not final tuning\nProvide initial Kp, Ti, Td estimates',
            cons:'Produce ~25% OS — usually needs refinement\nModern methods (IMC, SIMC) often preferred' },
        ].map(({ x, color, bg, title, pros, cons }) => (
          <g key={x}>
            <rect x={x+2} y={oy+PH+36} width={282} height={64} rx={3} fill={bg} stroke={color} strokeWidth="1"/>
            <text x={x+10} y={oy+PH+50} fontSize={9.5} fill={color} fontWeight="700">{title}</text>
            {pros.split('\n').map((l,j) => (
              <text key={j} x={x+10} y={oy+PH+63+j*13} fontSize={8.5} fill="#27ae60">✓ {l}</text>
            ))}
            {cons.split('\n').map((l,j) => (
              <text key={j} x={x+10} y={oy+PH+89+j*13} fontSize={8.5} fill="#c0392b">✗ {l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The ultimate gain method finds the stability boundary experimentally: increase proportional gain until the closed-loop output oscillates with constant amplitude, then read off the ultimate gain Ku and period Tu. The ZN formulas yield PID gains set to give approximately one-quarter decay ratio (25% overshoot per cycle reduction). The method cannot be applied when sustained oscillation is unsafe or physically impossible.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 5 — PID Bode Plot (P, PI, PD, PID overlaid)
// ─────────────────────────────────────────────────────────────────
function DiagramPIDBode() {
  const W = 900, H = 390;
  const Npts = 200, wMin = 0.05, wMax = 100;

  // Controller parameters
  const Kp=2, Ki=1, Kd=0.5, Nd=8; // derivative filter N=8

  // Compute Bode for each controller type
  const wArr = [];
  for (let i=0; i<=Npts; i++) {
    wArr.push(wMin * Math.pow(wMax/wMin, i/Npts));
  }

  const controllers = [
    {
      label:'P', color:'#555',
      mag: w => 20*Math.log10(Kp),
      phs: w => 0,
    },
    {
      label:'PI', color:'#2E75B6',
      mag: w => 20*Math.log10(Math.sqrt(Kp*Kp + (Ki/w)*(Ki/w) - 2*Kp*(Ki/w)*Math.sin(-Math.PI/2))),
      phs: w => {
        const re = Kp, im = -Ki/w;
        return Math.atan2(im, re) * 180/Math.PI;
      },
    },
    {
      label:'PD (filtered)', color:'#27ae60',
      mag: w => {
        const re = Kp + Kd*Nd*w*w/(w*w+Nd*Nd);
        const im = Kd*Nd*Nd*w/(w*w+Nd*Nd);
        return 20*Math.log10(Math.sqrt(re*re+im*im));
      },
      phs: w => {
        const re = Kp + Kd*Nd*w*w/(w*w+Nd*Nd);
        const im = Kd*Nd*Nd*w/(w*w+Nd*Nd);
        return Math.atan2(im,re)*180/Math.PI;
      },
    },
    {
      label:'PID (filtered)', color:'#c0392b',
      mag: w => {
        const re = Kp + Kd*Nd*w*w/(w*w+Nd*Nd);
        const im = -Ki/w + Kd*Nd*Nd*w/(w*w+Nd*Nd);
        return 20*Math.log10(Math.sqrt(re*re+im*im));
      },
      phs: w => {
        const re = Kp + Kd*Nd*w*w/(w*w+Nd*Nd);
        const im = -Ki/w + Kd*Nd*Nd*w/(w*w+Nd*Nd);
        return Math.atan2(im,re)*180/Math.PI;
      },
    },
  ];

  const PW=400, PH=140;
  const mOx=34, mOy=28, pOx=34, pOy=196;
  const swx = w   => mOx+36+(Math.log10(w/wMin)/Math.log10(wMax/wMin))*(PW-52);
  const smag = dB => mOy+PH-18-((Math.min(30,Math.max(-40,dB))+40)/70)*(PH-28);
  const sphs = ph => pOy+PH-18-((ph+180)/270)*(PH-28);

  const wTicks = [0.1,0.2,0.5,1,2,5,10,20,50,100];
  const magTicks= [-40,-20,0,20];
  const phsTicks= [-180,-90,-45,0,45,90];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>PID Controller Bode Plot — P, PI, PD, PID Frequency Response (Kp=2, Ki=1, Kd=0.5, N=8)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <clipPath id="bode-mag"><rect x={mOx+34} y={mOy+4} width={PW-52} height={PH-22}/></clipPath>
          <clipPath id="bode-phs"><rect x={pOx+34} y={pOy+4} width={PW-52} height={PH-22}/></clipPath>
        </defs>

        {/* Magnitude panel */}
        <rect x={mOx} y={mOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={mOx+PW/2} y={mOy-8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Magnitude |C(jω)| [dB]</text>

        {wTicks.map(wv => (
          <g key={wv}>
            <line x1={swx(wv)} y1={mOy+6} x2={swx(wv)} y2={mOy+PH-16} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={swx(wv)} y={mOy+PH-4} textAnchor="middle" fontSize={7.5} fill="#ccc">{wv}</text>
          </g>
        ))}
        {magTicks.map(mv => (
          <g key={mv}>
            <line x1={mOx+34} y1={smag(mv)} x2={mOx+PW-16} y2={smag(mv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={mOx+30} y={smag(mv)+3} textAnchor="end" fontSize={7.5} fill="#ccc">{mv}</text>
          </g>
        ))}
        <line x1={mOx+34} y1={mOy+PH-16} x2={mOx+PW-12} y2={mOy+PH-16} stroke="#aaa" strokeWidth="1.2"/>
        <line x1={mOx+34} y1={mOy+6}     x2={mOx+34}    y2={mOy+PH-16} stroke="#aaa" strokeWidth="1.2"/>

        {controllers.map(({ label, color, mag }) => {
          const d = wArr.map((w,i) => `${i===0?'M':'L'}${swx(w).toFixed(1)},${smag(mag(w)).toFixed(1)}`).join(' ');
          return <path key={label} d={d} fill="none" stroke={color} strokeWidth={label==='PID (filtered)'?2.5:1.8} clipPath="url(#bode-mag)"/>;
        })}

        {/* Phase panel */}
        <rect x={pOx} y={pOy} width={PW} height={PH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={pOx+PW/2} y={pOy-8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Phase ∠C(jω) [°]</text>

        {wTicks.map(wv => (
          <line key={wv} x1={swx(wv)} y1={pOy+6} x2={swx(wv)} y2={pOy+PH-16} stroke="#f5f5f5" strokeWidth="1"/>
        ))}
        {phsTicks.map(pv => (
          <g key={pv}>
            <line x1={pOx+34} y1={sphs(pv)} x2={pOx+PW-16} y2={sphs(pv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={pOx+30} y={sphs(pv)+3} textAnchor="end" fontSize={7.5} fill="#ccc">{pv}°</text>
          </g>
        ))}
        <line x1={pOx+34} y1={pOy+PH-16} x2={pOx+PW-12} y2={pOy+PH-16} stroke="#aaa" strokeWidth="1.2"/>
        <line x1={pOx+34} y1={pOy+6}     x2={pOx+34}    y2={pOy+PH-16} stroke="#aaa" strokeWidth="1.2"/>

        {controllers.map(({ label, color, phs }) => {
          const d = wArr.map((w,i) => `${i===0?'M':'L'}${swx(w).toFixed(1)},${sphs(phs(w)).toFixed(1)}`).join(' ');
          return <path key={label} d={d} fill="none" stroke={color} strokeWidth={label==='PID (filtered)'?2.5:1.8} clipPath="url(#bode-phs)"/>;
        })}
        <text x={pOx+36} y={pOy+PH-4} fontSize={7.5} fill="#aaa">ω [rad/s]</text>

        {/* ── RIGHT: Legend + key features ── */}
        <rect x={450} y={mOy} width={442} height={360} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={671} y={mOy+18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Controller Bode Characteristics</text>

        {/* Legend */}
        {controllers.map(({ label, color }, i) => (
          <g key={i}>
            <rect x={458} y={mOy+26+i*28} width={426} height={26} rx={2} fill={i%2===0?'#fafafa':'#fff'} stroke={color} strokeWidth="0.7"/>
            <line x1={464} y1={mOy+40+i*28} x2={490} y2={mOy+40+i*28} stroke={color} strokeWidth={label==='PID (filtered)'?2.5:1.8}/>
            <text x={496} y={mOy+44+i*28} fontSize={10} fill={color} fontWeight="700">{label}</text>
            <text x={610} y={mOy+44+i*28} fontSize={9}  fill="#555">
              {label==='P'?'Flat magnitude, zero phase (pure gain)':
               label==='PI'?'-20dB/dec at low ω, phase lag 0→-90° then recovers':
               label==='PD (filtered)'?'+20dB/dec at mid ω, phase lead up to +90°':
               'I: lag at low ω; D: lead at mid ω; combined "saddle"'}
            </text>
          </g>
        ))}

        {/* Key insights */}
        {[
          { y:mOy+142, fill:'#FEF0F0', bc:'#c0392b',
            title:'PI: Integral lag at low frequencies',
            body:['C_PI(s) = Kp(1+1/Ti·s) → zero at s=-1/Ti',
                  'Boosts low-ω gain: eliminates steady-state error',
                  'Phase lag: reduces phase margin — destabilising'] },
          { y:mOy+214, fill:'#f0fff4', bc:'#27ae60',
            title:'PD: Derivative lead at mid frequencies',
            body:['C_PD(s) = Kp(1+Td·s/(1+Td/N·s)) → zero before pole',
                  'Phase lead: improves phase margin and damping',
                  'Magnitude boost: amplifies high-frequency noise'] },
          { y:mOy+286, fill:'#eaf3ff', bc:'#2E75B6',
            title:'PID: Combined lead-lag controller',
            body:['Adds one zero pair and one integrator',
                  'Phase "saddle": lag at low ω, lead at mid ω',
                  'Net effect: improve PM while eliminating e_ss'] },
        ].map(({ y, fill, bc, title, body }) => (
          <g key={y}>
            <rect x={458} y={y} width={426} height={68} rx={3} fill={fill} stroke={bc} strokeWidth="1"/>
            <text x={466} y={y+14} fontSize={10} fill="#7B0D0D" fontWeight="700">{title}</text>
            {body.map((l,j) => (
              <text key={j} x={466} y={y+28+j*14} fontSize={9} fill="#333" fontFamily="'Courier New',monospace">{l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>In the frequency domain, proportional control is a flat gain line. Adding integral (PI) boosts gain at low frequencies (eliminating steady-state error) but introduces phase lag that can reduce stability margins. Adding derivative (PD) introduces a phase lead that improves damping but amplifies noise. The full PID combines both: integral for zero SSE and derivative for phase-lead compensation, with the filtered derivative pole (N) limiting high-frequency gain.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 6 — Anti-Windup (computed with and without)
// ─────────────────────────────────────────────────────────────────
function DiagramAntiWindup() {
  const W = 900, H = 360;
  const dt = 0.02, tMax = 20;

  // Reference: step from 0 to 3 at t=1, back to 1 at t=10
  const ref = (t) => t < 1 ? 0 : t < 10 ? 3 : 1;

  const Kp=2, Ki=3, Kd=0;

  // WITHOUT anti-windup
  const noAW = simulatePID_noAW(Kp, Ki, Kd, tMax, dt, ref);
  // WITH anti-windup (built into simulatePID)
  const withAW = simulatePID(Kp, Ki, Kd, tMax, dt, ref, 0);

  const PW=410, PH=200;
  const ox=20, oy=28;
  const sx = t  => ox+34+(t/tMax)*(PW-50);
  const sy = yv => oy+PH-20-Math.max(Math.min(yv,3.6),-0.3)*(PH-34)/3.8;
  const su = u  => oy+PH+22+16-Math.max(Math.min(u,12),-4)*(60)/14;

  const refPath  = noAW.map(({t},i) => `${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(ref(t)).toFixed(1)}`).join(' ');
  const noAWPath = noAW.map(({t,y},i) => `${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');
  const awPath   = withAW.map(({t,y},i) => `${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');

  // Control effort paths
  const noAWuPath= noAW.map(({t,u},i)   => `${i===0?'M':'L'}${sx(t).toFixed(1)},${su(u).toFixed(1)}`).join(' ');
  const awuPath  = withAW.map(({t,u},i) => `${i===0?'M':'L'}${sx(t).toFixed(1)},${su(u).toFixed(1)}`).join(' ');

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Anti-Windup — Integrator Saturation Problem &amp; Solutions</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="aw-blk" color="#aaa"/>
          <clipPath id="aw-clip"><rect x={54} y={30} width={PW-46} height={PH-18}/></clipPath>
          <clipPath id="aw-u-clip"><rect x={54} y={oy+PH+20} width={PW-46} height={68}/></clipPath>
        </defs>

        {/* ── LEFT: Output comparison ── */}
        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox+PW/2} y={oy-8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Output y(t) — With vs Without Anti-Windup</text>

        {/* Grid */}
        {[5,10,15,20].map(tv=>(
          <line key={tv} x1={sx(tv)} y1={oy+6} x2={sx(tv)} y2={oy+PH-18} stroke="#f8f8f8" strokeWidth="1"/>
        ))}
        {[1,2,3].map(yv=>(
          <line key={yv} x1={ox+32} y1={sy(yv)} x2={ox+PW-16} y2={sy(yv)} stroke="#f5f5f5" strokeWidth="1"/>
        ))}

        <line x1={ox+32} y1={oy+PH-18} x2={ox+PW-10} y2={oy+PH-18} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#aw-blk)"/>
        <line x1={ox+32} y1={oy+PH-18} x2={ox+32}    y2={oy+4}     stroke="#aaa" strokeWidth="1.2" markerEnd="url(#aw-blk)"/>
        <text x={ox+PW-6} y={oy+PH-14} fontSize={8} fill="#aaa">t</text>

        {/* Reference */}
        <path d={refPath}  fill="none" stroke="#aaa" strokeWidth="1.5" strokeDasharray="6,3" clipPath="url(#aw-clip)"/>
        {/* Without AW */}
        <path d={noAWPath} fill="none" stroke="#c0392b" strokeWidth="2.2" clipPath="url(#aw-clip)"/>
        {/* With AW */}
        <path d={awPath}   fill="none" stroke="#27ae60" strokeWidth="2.2" clipPath="url(#aw-clip)"/>

        {/* Windup annotation */}
        <text x={sx(13)} y={sy(2.8)} fontSize={9} fill="#c0392b" fontWeight="700">Windup: overshoot</text>
        <text x={sx(13)} y={sy(2.65)} fontSize={8.5} fill="#c0392b">integrator kept growing</text>
        <text x={sx(13)} y={sy(2.50)} fontSize={8.5} fill="#c0392b">while saturated</text>

        {/* Legend */}
        <line x1={ox+36} y1={oy+PH-8} x2={ox+56} y2={oy+PH-8} stroke="#aaa"    strokeWidth="1.5" strokeDasharray="5,3"/>
        <text x={ox+60}  y={oy+PH-4}  fontSize={8.5} fill="#aaa">Reference r(t)</text>
        <line x1={ox+160} y1={oy+PH-8} x2={ox+180} y2={oy+PH-8} stroke="#c0392b" strokeWidth="2"/>
        <text x={ox+184} y={oy+PH-4}   fontSize={8.5} fill="#c0392b">No Anti-Windup</text>
        <line x1={ox+280} y1={oy+PH-8} x2={ox+300} y2={oy+PH-8} stroke="#27ae60" strokeWidth="2"/>
        <text x={ox+304} y={oy+PH-4}   fontSize={8.5} fill="#27ae60">With Anti-Windup</text>

        {/* ── LEFT bottom: Actuator u(t) ── */}
        <rect x={ox} y={oy+PH+16} width={PW} height={76} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox+PW/2} y={oy+PH+28} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontWeight="700">Control Effort u(t) [saturates at ±10]</text>
        <line x1={ox+32}  y1={oy+PH+16+60} x2={ox+PW-10} y2={oy+PH+16+60} stroke="#aaa" strokeWidth="1"/>
        <line x1={ox+32}  y1={oy+PH+16+10} x2={ox+PW-10} y2={oy+PH+16+10} stroke="#e07b00" strokeWidth="1.2" strokeDasharray="4,3" opacity="0.6"/>
        <text x={ox+36}  y={oy+PH+16+8} fontSize={8} fill="#e07b00">u=10 (saturation)</text>
        <path d={noAWuPath} fill="none" stroke="#c0392b" strokeWidth="1.8" clipPath="url(#aw-u-clip)"/>
        <path d={awuPath}   fill="none" stroke="#27ae60" strokeWidth="1.8" clipPath="url(#aw-u-clip)"/>

        {/* ── RIGHT: Explanation ── */}
        <rect x={446} y={oy} width={444} height={356} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={668} y={oy+18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">The Windup Problem &amp; Solutions</text>

        {[
          { y:oy+28, fill:'#FEF0F0', bc:'#c0392b', title:'What is Integrator Windup?',
            body:['When the actuator saturates (u hits limit),',
                  'the control output cannot follow the controller demand.',
                  'But the integrator keeps accumulating error.',
                  '→ Integral grows to a very large value.',
                  'When error reduces, the integrator must "unwind"',
                  'before control becomes normal → large overshoot!'] },
          { y:oy+134, fill:'#f0fff4', bc:'#27ae60', title:'Anti-Windup: Clamping',
            body:['Simply stop integrating when actuator saturates:',
                  'if |u| > u_max: integral += 0  (freeze integrator)',
                  'Simple and effective for most applications.',
                  'Also: back-calculation — subtract excess from integral'] },
          { y:oy+218, fill:'#eaf3ff', bc:'#2E75B6', title:'Anti-Windup: Back-Calculation',
            body:['Add correction: dI/dt += (u_sat - u_unsat)/Tt',
                  'Tt = tracking time constant (tune separately)',
                  'Smoothly unwinds the integrator when saturated.',
                  'More elegant; standard in industrial controllers'] },
          { y:oy+302, fill:'#fff5e6', bc:'#e07b00', title:'Other Windup Countermeasures',
            body:['Conditional integration: only integrate when e is small',
                  'Integral gain scheduling: reduce Ki during large errors',
                  'Setpoint weighting: apply step to P only, not I'] },
        ].map(({ y, fill, bc, title, body }) => (
          <g key={y}>
            <rect x={454} y={y} width={428} height={body.length*16+22} rx={3} fill={fill} stroke={bc} strokeWidth="1"/>
            <text x={462} y={y+15} fontSize={10} fill="#7B0D0D" fontWeight="700">{title}</text>
            {body.map((l,j) => (
              <text key={j} x={462} y={y+29+j*15} fontSize={9} fill="#333">{l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Integrator windup occurs when the actuator saturates: the integral term keeps growing even though the actuator is stuck at its limit. When the reference drops back, the accumulated integral drives a large overshoot before it unwinds. Anti-windup (clamping or back-calculation) prevents this by freezing or unwinding the integrator during saturation — the green curve shows the dramatically improved response.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 7 — Derivative Filtering & Noise
// ─────────────────────────────────────────────────────────────────
function DiagramDerivativeFilter() {
  const W = 900, H = 360;
  const dt = 0.005, tMax = 8;

  // Add measurement noise to the reference
  // Step response with noisy measurement
  const seed = 42;
  const lcg = (s) => { let x=s; return () => { x=(x*1664525+1013904223)>>>0; return (x/0xFFFFFFFF)*2-1; }; };
  const rng = lcg(seed);

  const noiseLevel = 0.08;

  const simWithNoise = (Kp, Ki, Kd_val, Nd_val) => {
    const N = Math.round(tMax/dt);
    const pts = [];
    let x1=0, x2=0, integral=0, prevErr=0, dFilt=0;
    const fa = Nd_val > 0 ? (Nd_val*dt)/(1+Nd_val*dt) : 1;
    for (let i=0; i<=N; i++) {
      const t = i*dt;
      const r = t>0.5 ? 1 : 0;
      const noise = noiseLevel * rng();
      const y = x2 + noise; // noisy measurement
      const err = r - y;
      integral += err*dt;
      const rawD = (err - prevErr)/dt;
      dFilt = dFilt + fa*(rawD - dFilt);
      const u = Math.max(-10, Math.min(10, Kp*err + Ki*integral + Kd_val*dFilt));
      pts.push({ t, y: x2, u, yNoisy: y }); // store clean y
      prevErr = err;
      const dx1=-x1+u, dx2=-2*x2+2*x1;
      x1+=dx1*dt; x2+=dx2*dt;
    }
    return pts;
  };

  // Compare: no derivative vs unfiltered D vs filtered D
  const pNoD   = simWithNoise(4, 1, 0,    0);
  const pRawD  = simWithNoise(4, 1, 1.5,  0);   // unfiltered (Nd→∞ approximated as 0)
  const pFiltD = simWithNoise(4, 1, 1.5,  10);  // filtered N=10

  // Control effort for noise comparison
  const PW=400, PH=190;
  const ox=20, oy=28;
  const sx = t  => ox+34+(t/tMax)*(PW-50);
  const sy = yv => oy+PH-20-Math.max(Math.min(yv,1.4),-0.1)*(PH-36)/1.5;
  const su = uv => oy+PH+24+50-Math.max(Math.min(uv,12),-3)*50/14;

  const refPath  = pNoD.map(({t},i) => `${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(t>0.5?1:0).toFixed(1)}`).join(' ');
  const noDPath  = pNoD.map(({t,y},i)   => `${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');
  const rawDPath = pRawD.map(({t,y},i)  => `${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');
  const filtPath = pFiltD.map(({t,y},i) => `${i===0?'M':'L'}${sx(t).toFixed(1)},${sy(y).toFixed(1)}`).join(' ');

  const rawUPath = pRawD.map(({t,u},i)  => `${i===0?'M':'L'}${sx(t).toFixed(1)},${su(u).toFixed(1)}`).join(' ');
  const filtUPath= pFiltD.map(({t,u},i) => `${i===0?'M':'L'}${sx(t).toFixed(1)},${su(u).toFixed(1)}`).join(' ');

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Derivative Filtering — Managing Noise Amplification in the D Term</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="df-blk" color="#aaa"/>
          <clipPath id="df-y"><rect x={54} y={30} width={PW-46} height={PH-16}/></clipPath>
          <clipPath id="df-u"><rect x={54} y={oy+PH+22} width={PW-46} height={68}/></clipPath>
        </defs>

        {/* Output plot */}
        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox+PW/2} y={oy-8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Step Response with Measurement Noise</text>

        {[2,4,6,8].map(tv=>(
          <line key={tv} x1={sx(tv)} y1={oy+6} x2={sx(tv)} y2={oy+PH-18} stroke="#f8f8f8" strokeWidth="1"/>
        ))}
        <line x1={ox+32} y1={sy(1)} x2={ox+PW-16} y2={sy(1)} stroke="#27ae60" strokeWidth="1.2" strokeDasharray="5,4" opacity="0.6"/>
        <line x1={ox+32} y1={oy+PH-18} x2={ox+PW-10} y2={oy+PH-18} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#df-blk)"/>
        <line x1={ox+32} y1={oy+PH-18} x2={ox+32}    y2={oy+4}     stroke="#aaa" strokeWidth="1.2" markerEnd="url(#df-blk)"/>

        <path d={refPath}  fill="none" stroke="#aaa" strokeWidth="1.2" strokeDasharray="5,3" clipPath="url(#df-y)"/>
        <path d={noDPath}  fill="none" stroke="#555"    strokeWidth="2"   clipPath="url(#df-y)"/>
        <path d={rawDPath} fill="none" stroke="#c0392b" strokeWidth="1.5" clipPath="url(#df-y)"/>
        <path d={filtPath} fill="none" stroke="#27ae60" strokeWidth="2"   clipPath="url(#df-y)"/>

        {/* Legend */}
        {[
          {c:'#aaa',    d:'5,3', lbl:'Reference'},
          {c:'#555',    d:'none',lbl:'PI only (no D)'},
          {c:'#c0392b', d:'none',lbl:'PID unfiltered'},
          {c:'#27ae60', d:'none',lbl:'PID filtered N=10'},
        ].map(({c,d,lbl},i) => (
          <g key={i}>
            <line x1={ox+36+i*100} y1={oy+PH-8} x2={ox+52+i*100} y2={oy+PH-8}
              stroke={c} strokeWidth={d==='none'?2:1.5} strokeDasharray={d==='none'?undefined:d}/>
            <text x={ox+56+i*100} y={oy+PH-4} fontSize={8} fill={c}>{lbl}</text>
          </g>
        ))}

        {/* Control effort */}
        <rect x={ox} y={oy+PH+16} width={PW} height={76} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox+PW/2} y={oy+PH+28} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontWeight="700">Control Effort u(t) — Noise Injected into Actuator</text>
        <line x1={ox+32} y1={oy+PH+16+65} x2={ox+PW-10} y2={oy+PH+16+65} stroke="#aaa" strokeWidth="1"/>
        <path d={rawUPath}  fill="none" stroke="#c0392b" strokeWidth="1.5" clipPath="url(#df-u)"/>
        <path d={filtUPath} fill="none" stroke="#27ae60" strokeWidth="1.5" clipPath="url(#df-u)"/>
        <text x={ox+36} y={oy+PH+16+46} fontSize={8} fill="#c0392b">Unfiltered D: massive u spikes</text>
        <text x={ox+36} y={oy+PH+16+58} fontSize={8} fill="#27ae60">Filtered D: smooth control effort</text>

        {/* ── RIGHT: Explanation ── */}
        <rect x={438} y={oy} width={452} height={356} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={664} y={oy+18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Derivative Filtering — Theory &amp; Practice</text>

        {[
          { y:oy+26,  fill:'#FEF0F0', bc:'#c0392b', title:'Why Unfiltered D Is Dangerous',
            body:['Ideal derivative: C_D(s) = Kd·s',
                  '|Kd·jω| = Kd·ω → gain increases without limit',
                  'High-frequency noise → huge control signal spikes',
                  'Actuator wear, mechanical damage, system instability'] },
          { y:oy+118, fill:'#f0fff4', bc:'#27ae60', title:'First-Order Derivative Filter',
            body:['C_Df(s) = Kd·Nd·s / (s + Nd)',
                  'Nd = filter coefficient (typically 5–20)',
                  'At low ω: acts like pure derivative',
                  'At high ω: magnitude rolls off → noise attenuated',
                  'Pole at s = -Nd limits high-frequency gain to Kd·Nd'] },
          { y:oy+228, fill:'#eaf3ff', bc:'#2E75B6', title:'Derivative on Measurement (D-o-M)',
            body:['Apply D only to y(t), not to e(t):',
                  'u = Kp·e + Ki∫e - Kd·dy/dt',
                  'Avoids derivative kick on setpoint steps',
                  'Reference step: D term = 0 (y smooth initially)',
                  'Standard in modern industrial PID implementation'] },
          { y:oy+310, fill:'#fff5e6', bc:'#e07b00', title:'Choosing Filter Coefficient N',
            body:['Small N (2-5): heavy filtering, derivative loses effectiveness',
                  'Large N (20-50): light filtering, more noise passes through',
                  'Typical choice: N = 10 (good compromise)'] },
        ].map(({ y, fill, bc, title, body }) => (
          <g key={y}>
            <rect x={446} y={y} width={436} height={body.length*16+22} rx={3} fill={fill} stroke={bc} strokeWidth="1"/>
            <text x={454} y={y+15} fontSize={10} fill="#7B0D0D" fontWeight="700">{title}</text>
            {body.map((l,j) => (
              <text key={j} x={454} y={y+29+j*15} fontSize={9} fill="#333" fontFamily="'Courier New',monospace">{l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Measurement noise is amplified by the derivative term because differentiation boosts high frequencies. The unfiltered D term (red) shows chaotic control effort that wears actuators. A first-order filter with coefficient N=10 (green) suppresses noise while preserving derivative action for damping. Derivative-on-measurement (computing Kd·dy/dt instead of Kd·de/dt) also eliminates the "derivative kick" caused by step setpoint changes.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 8 — Tuning Methods Comparison & Practical Guidelines
// ─────────────────────────────────────────────────────────────────
function DiagramTuningComparison() {
  const W = 900, H = 400;
  const dt=0.02, tMax=16;

  // Compare four tuned responses on the same plant
  const configs = [
    { label:'P only (Kp=5)',    Kp:5,   Ki:0,   Kd:0,   color:'#aaa' },
    { label:'PI (Kp=3, Ki=1)',  Kp:3,   Ki:1,   Kd:0,   color:'#2E75B6' },
    { label:'PD (Kp=5, Kd=0.8)',Kp:5,   Ki:0,   Kd:0.8, color:'#27ae60' },
    { label:'PID (ZN-tuned)',    Kp:4,   Ki:2,   Kd:0.5, color:'#c0392b' },
    { label:'PID (conservative)',Kp:2.5,Ki:1.2, Kd:0.8, color:'#e07b00' },
  ];

  const allPts = configs.map(({ Kp, Ki, Kd, color, label }) => {
    const pts = simulatePID(Kp, Ki, Kd, tMax, dt, 1, 10);
    return { pts, color, label, Kp, Ki, Kd };
  });

  const PW=430, PH=240;
  const ox=20, oy=28;
  const sx = t  => ox+34+(t/tMax)*(PW-50);
  const sy = yv => oy+PH-22-Math.max(Math.min(yv,2),-0.05)*(PH-38)/2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Tuning Comparison — P / PI / PD / PID (Aggressive vs Conservative)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="tc-blk" color="#aaa"/>
          <clipPath id="tc-clip"><rect x={54} y={30} width={PW-46} height={PH-22}/></clipPath>
        </defs>

        {/* Plot */}
        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox+PW/2} y={oy-8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Step Response Comparison — Same Plant, Different Controllers</text>

        {[4,8,12,16].map(tv=>(
          <line key={tv} x1={sx(tv)} y1={oy+6} x2={sx(tv)} y2={oy+PH-20} stroke="#f8f8f8" strokeWidth="1"/>
        ))}
        {[0.5,1.0,1.5,2.0].map(yv=>(
          <line key={yv} x1={ox+32} y1={sy(yv)} x2={ox+PW-16} y2={sy(yv)} stroke="#f5f5f5" strokeWidth="1"/>
        ))}
        <line x1={ox+32} y1={sy(1)} x2={ox+PW-16} y2={sy(1)} stroke="#27ae60" strokeWidth="1.3" strokeDasharray="6,4" opacity="0.7"/>
        <text x={ox+PW-12} y={sy(1)-4} fontSize={8.5} fill="#27ae60">r=1</text>

        <line x1={ox+32} y1={oy+PH-20} x2={ox+PW-8}  y2={oy+PH-20} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#tc-blk)"/>
        <line x1={ox+32} y1={oy+PH-20} x2={ox+32}    y2={oy+4}     stroke="#aaa" strokeWidth="1.2" markerEnd="url(#tc-blk)"/>
        <text x={ox+PW-4} y={oy+PH-16} fontSize={8} fill="#aaa">t</text>

        {allPts.map(({ pts, color }, i) => {
          const d = mkPath(pts, sx, sy, 'y');
          return <path key={i} d={d} fill="none" stroke={color} strokeWidth={i===3||i===4?2.5:1.8} clipPath="url(#tc-clip)"/>;
        })}

        {/* Legend */}
        {allPts.map(({ color, label }, i) => (
          <g key={i}>
            <line x1={ox+6} y1={oy+PH+2+i*14} x2={ox+22} y2={oy+PH+2+i*14} stroke={color} strokeWidth={i===3||i===4?2.5:1.8}/>
            <text x={ox+26} y={oy+PH+6+i*14} fontSize={8.5} fill={color}>{label}</text>
          </g>
        ))}

        {/* ── RIGHT: Practical guidelines ── */}
        <rect x={464} y={oy} width={426} height={396} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={677} y={oy+18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Practical PID Tuning Guidelines</text>

        {[
          { y:oy+28, color:'#c0392b', bg:'#FEF0F0', title:'Manual Tuning Steps (Iterative)',
            items:['Start: Ki=0, Kd=0, Kp small',
                   'Increase Kp until response is fast with ~10-20% OS',
                   'Add Ki slowly until steady-state error → 0',
                   'Add Kd to reduce overshoot and settle faster',
                   'Iterate: adjust all three until spec is met'] },
          { y:oy+128, color:'#2E75B6', bg:'#eaf3ff', title:'IMC-Based PID (Model-Based)',
            items:['Requires FOPDT model: K, L, T from step test',
                   'Choose closed-loop TC: λ (≥ 0.25L, tunable)',
                   'Kp = T/(K(λ+L)),  Ti = T,  Td = L/2',
                   'Single tuning knob λ: larger = more conservative',
                   'SIMC rules (Skogestad): λ = max(T, 8L)/5'] },
          { y:oy+228, color:'#27ae60', bg:'#f0fff4', title:'Gain Margin / Phase Margin Tuning',
            items:['Open Bode plot of G(s)C(s)',
                   'Target: PM ≥ 45° (comfortable), ≥ 60° (conservative)',
                   'Target: GM ≥ 6 dB',
                   'Adjust Kp to place gain crossover at desired ωc',
                   'Use PI/PD to shape phase margin at crossover'] },
          { y:oy+316, color:'#e07b00', bg:'#fff5e6', title:'Common Pitfalls',
            items:['Too aggressive Ki: oscillation, windup',
                   'Too large Kd (unfiltered): noise → actuator damage',
                   'No anti-windup: huge overshoot after saturation',
                   'Tuning at one OP only: may fail at others (gain schedule)',
                   'Ignoring actuator limits in simulation'] },
        ].map(({ y, color, bg, title, items }) => (
          <g key={y}>
            <rect x={472} y={y} width={410} height={items.length*18+22} rx={3} fill={bg} stroke={color} strokeWidth="1.2"/>
            <text x={480} y={y+15} fontSize={10} fill={color} fontWeight="700">{title}</text>
            {items.map((item, j) => (
              <text key={j} x={480} y={y+30+j*17} fontSize={9} fill="#333">▸ {item}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Comparing five controller configurations on the same plant shows the trade-offs clearly: P-only leaves a residual error; PI adds integral action eliminating it but with more oscillation; PD improves transient without addressing offset; aggressive PID (ZN-tuned) is fast but overshoots; conservative PID sacrifices some speed for a well-damped, smooth response. The "best" choice depends on the application's tolerance for overshoot, settling time, and noise.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Export
// ─────────────────────────────────────────────────────────────────
export default function PIDControl() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Control Systems — PID Control &amp; Tuning</h1>
      <p style={S.p}>
        The Proportional-Integral-Derivative (PID) controller is the most widely deployed feedback control algorithm in the world — estimated to be running in more than 90% of all industrial control loops. Its enduring success comes from combining just three terms that collectively address the three dimensions of control quality: proportional action reacts to the current error, integral action eliminates persistent offset by accumulating past error, and derivative action anticipates future error by reacting to its rate of change. Understanding PID — its structure, frequency-domain behaviour, practical limitations, and systematic tuning methods — is the cornerstone of applied control engineering.
      </p>

      <InfoBox title="Core Concepts at a Glance" items={[
        "u(t) = Kp·e(t) + Ki·∫e(τ)dτ + Kd·de/dt — the three-term control law",
        "Laplace domain: C(s) = Kp + Ki/s + Kd·s = (Kd·s² + Kp·s + Ki)/s",
        "Kp: reduces error magnitude but leaves offset in Type 0 systems",
        "Ki: eliminates steady-state error (adds integrator, Type 0→1); risks windup",
        "Kd: damps overshoot by reacting to error rate; amplifies noise — always filter",
        "ZN open-loop: fit FOPDT model (K, L, T) from step test → table lookup",
        "ZN ultimate gain: find Ku and Tu from closed-loop oscillation → table lookup",
        "Anti-windup: freeze or back-calculate integrator when actuator saturates",
        "Filtered derivative: C_Df(s)=Kd·Nd·s/(s+Nd) limits high-frequency gain",
        "Modern methods: IMC/SIMC give single-parameter tuning with stability guarantee",
      ]}/>

      <h2 style={S.h2}>PID Structure &amp; Three Forms</h2>
      <p style={S.p}>
        The PID controller computes its output from the error signal e(t)=r(t)−y(t) using three parallel branches. Each branch contributes a distinct role: the proportional branch provides immediate response proportional to the current error; the integral branch accumulates error over time to eliminate any persistent offset; the derivative branch differentiates the error to provide anticipatory action and damping. All three forms — parallel, standard (ISA), and series — describe the same controller but with different parametrisations.
      </p>
      <DiagramPIDStructure/>
      <Table2 rows={[
        ["Control law (time domain)","u(t) = Kp·e(t) + Ki·∫₀ᵗ e(τ)dτ + Kd·de(t)/dt\ne(t) = r(t) − y(t)  (error signal)"],
        ["Transfer function C(s)","C(s) = Kp + Ki/s + Kd·s = (Kd·s² + Kp·s + Ki) / s\nAdds a zero pair and one integrator to the open-loop"],
        ["Parallel form","Parameters: Kp, Ki, Kd  (independent)\nMost common in digital implementation and textbooks"],
        ["Standard (ISA) form","C(s) = Kp[1 + 1/(Ti·s) + Td·s]\nTi = Kp/Ki (integral time / reset time)  |  Td = Kd/Kp (derivative time)\nStandard in industrial DCS / PLC implementations"],
        ["Series (interacting) form","C(s) = Kp(1 + 1/(Ti·s))(1 + Td·s)\nDifferent poles/zeros from parallel; used in older analogue instruments\nTuning with Kp affects I and D terms — interactions"],
        ["Effect on system type","Adding Ki/s adds a pole at s=0 → system type increases by 1\nType 0 plant + PI controller → Type 1 closed loop → zero step error"],
        ["Pole-zero structure","Parallel PID: two finite zeros and one pole at origin\nZeros at: s = [−Kp ± √(Kp²−4KdKi)] / (2Kd)\nDesigner places zeros by choosing Kp, Ki, Kd"],
        ["Practical modifications","Derivative on measurement (not on error): avoids derivative kick\nSetpoint weighting b,c: u = Kp(b·r−y) + Ki∫e − Kd·dy/dt\nTwo degrees of freedom: reference prefilter + feedback controller"],
      ]}/>

      <h2 style={S.h2}>Individual P, I, D Term Effects</h2>
      <p style={S.p}>
        Before combining all three terms, it is instructive to observe each one in isolation on the same plant. The proportional term alone produces a fast response but leaves a permanent offset (steady-state error) for any Type 0 plant. Adding the integral term eliminates this offset, at the cost of increased oscillation with high Ki. Adding the derivative term to a PI controller progressively reduces overshoot and settling time, but at the risk of noise amplification.
      </p>
      <DiagramIndividualEffects/>
      <Table2 rows={[
        ["P term effect","Faster rise time as Kp increases\nSteady-state error decreases: e_ss = 1/(1+Kp) for Type 0\nHigher Kp → more overshoot and reduced stability margin\nCannot achieve zero steady-state error for step (Type 0 plant)"],
        ["I term effect","Any Ki>0 → zero steady-state error to step (regardless of magnitude)\nLarger Ki → faster error correction but more overshoot\nVery large Ki → instability (phase lag from integrator)\nRisks integrator windup when actuator saturates"],
        ["D term effect","Reduces overshoot: derivative anticipates the error peak and brakes early\nReduces settling time by damping the oscillation\nNo effect on steady-state error (D term = 0 at steady state)\nAmplifies measurement noise — must be filtered"],
        ["Interaction effects","Increasing Kp allows smaller Ki for same SSE correction\nIncreasing Kd allows higher Kp without instability\nAll three parameters interact — iterative tuning needed\nEach change affects rise time, overshoot, and settling simultaneously"],
        ["Dominant pole design","Desired poles: s = −ζωₙ ± jωd\nPID has two zeros: place to cancel (or improve) existing slow plant poles\nZero placement: Kp/Kd ± j√(Ki/Kd − (Kp/2Kd)²)\nControl system design = pole-zero placement by choosing Kp, Ki, Kd"],
        ["Rule of thumb starting gains","Kp ≈ 0.5 × (Kp at oscillation boundary)\nKi ≈ Kp / (2 × dominant time constant)\nKd ≈ Kp × (dominant time constant) / 8"],
      ]}/>

      <h2 style={S.h2}>Ziegler-Nichols Step (Open-Loop) Tuning</h2>
      <p style={S.p}>
        The Ziegler-Nichols open-loop (or process reaction) method was published in 1942 and remains widely known as a quick initial tuning procedure. It approximates the plant as a first-order-plus-dead-time (FOPDT) model G(s) ≈ K·e^(−Ls)/(Ts+1) by fitting a tangent line to the inflection point of the open-loop step response. From the three parameters K (process gain), L (dead time), and T (time constant), a lookup table gives initial PID gains.
      </p>
      <DiagramZNStep/>
      <Table2 rows={[
        ["FOPDT model","G(s) ≈ K·e^(−Ls)/(Ts+1)\nK = ΔOutput/ΔInput at steady state\nL = dead time (tangent crosses zero)\nT = time constant (tangent from L to K·u intersection)"],
        ["Finding L and T graphically","Apply step input u, record output y(t)\nDraw tangent line at inflection (maximum slope) point\nL = where tangent crosses initial output level\nT = where tangent crosses final output level, minus L"],
        ["ZN P controller","Kp = T/(K·L)\nFastest P-only tuning from FOPDT model\nExpect large overshoot"],
        ["ZN PI controller","Kp = 0.9T/(K·L),  Ti = L/0.3  →  Ki = Kp/Ti\nIntegral added; slower than ZN-P"],
        ["ZN PID controller","Kp = 1.2T/(K·L),  Ti = 2L,  Td = 0.5L\nKi = Kp/(2L),  Kd = 0.6L·T/(K·L)\nTypically ~25% overshoot — aggressive"],
        ["Detuning factor","Multiply Kp by 0.5–0.7 for more conservative response\nReduce Ki by same factor to maintain stability\nDetuning factor selection is application-dependent"],
        ["L/T ratio significance","L/T < 0.1: easy to control (small lag ratio)\nL/T ≈ 0.3: moderate difficulty\nL/T > 1.0: difficult — large dead time relative to dynamics\nZN formulas work best for L/T in range 0.1–0.5"],
        ["SIMC rules (Skogestad)","Improved single-tuning-parameter rules based on IMC\nPID: Kp=T/(K(λ+L)), Ti=min(T,4(λ+L)), Td=L/2\nλ = desired closed-loop TC; set λ=max(T,8L)/5 for robustness\nMore conservative than ZN; clearer stability guarantee"],
      ]}/>

      <h2 style={S.h2}>Ziegler-Nichols Ultimate Gain Method</h2>
      <p style={S.p}>
        The ultimate gain method is the closed-loop companion to the step test. Rather than modelling the plant, it directly finds the stability boundary of the closed-loop system. By increasing the proportional gain until continuous oscillation is observed, the engineer identifies the ultimate gain Ku and period Tu — the two pieces of information needed to set PID parameters. The resulting controller is designed to give approximately a quarter-decay-ratio (25% overshoot reduction per cycle) response.
      </p>
      <DiagramZNUltimate/>
      <Table2 rows={[
        ["Ultimate gain Ku","Kp value at which closed-loop with P-only control just sustains oscillation\nEquivalent to: open-loop gain at −180° phase frequency\nKu = 1/|G(jω₁₈₀)| where G is plant transfer function"],
        ["Ultimate period Tu","Period of the sustained oscillation at Ku\nTu = 2π/ω₁₈₀ where ω₁₈₀ is the phase crossover frequency"],
        ["ZN ultimate PID","Kp = 0.6Ku,  Ti = Tu/2,  Td = Tu/8\nKi = Kp/Ti = 1.2Ku/Tu,  Kd = 0.075KuTu"],
        ["Pessen integral rule","Modified ZN: Kp=0.7Ku, Ti=Tu/2.5, Td=0.15Tu\nLess overshoot than standard ZN ultimate"],
        ["No overshoot (NOR)","Kp=0.2Ku, Ti=Tu/2, Td=Tu/3\nVery conservative — minimal overshoot, slower settling"],
        ["Limitations","Cannot apply if plant is open-loop unstable\nCannot apply if sustained oscillation is physically dangerous\nIntegrating plants: steady-state not achieved with P-only\nTime-delay dominant plants: method gives poor results"],
        ["Frequency domain equivalence","Ku and Tu found from Bode: Ku = 1/|G(jωpc)|, Tu = 2π/ωpc\nωpc = phase crossover frequency (phase = −180°)\nSame information as gain margin and phase crossover"],
        ["Quarter decay ratio","ZN aims for output amplitude ratio of 4:1 per oscillation cycle\nNot a modern performance standard (too much overshoot)\nUse as starting point; refine to meet actual specifications"],
      ]}/>

      <h2 style={S.h2}>PID Bode Plot &amp; Frequency Domain</h2>
      <p style={S.p}>
        Analysing PID in the frequency domain reveals exactly how each term reshapes the loop gain and phase. The integral term adds a pole at the origin (boosting low-frequency gain but adding phase lag); the derivative term adds a zero (boosting mid-frequency gain and adding phase lead); together they can simultaneously eliminate steady-state error and increase the phase margin — if properly tuned.
      </p>
      <DiagramPIDBode/>
      <Table2 rows={[
        ["P Bode plot","Flat gain: |C(jω)| = Kp dB (all frequencies)\nZero phase shift: ∠C(jω) = 0°\nSets gain crossover frequency without affecting phase margin"],
        ["PI Bode plot","|C_PI| rises as ω→0 (integral effect): +20 dB/decade low-ω\nPhase: −90° at low ω, recovering toward 0° above ωi=Ki/Kp\nZero at s=−Ki/Kp; phase never fully recovers → reduces PM"],
        ["PD Bode plot (filtered)","|C_PD| rises at mid-frequencies (derivative hump)\nPhase lead: positive phase angle between ωz=Kp/Kd and ωp=Nd·Kp/Kd\nMaximum phase lead: φmax = arcsin((N−1)/(N+1)) at ωmax=√(ωz·ωp)"],
        ["PID Bode plot","Combined: lag at low ω (I), lead at mid ω (D), roll-off at high ω (filter)\nPhase starts at −90° (integral), rises to peak positive, then drops\n'Saddle'-shaped phase curve — characteristic PID signature"],
        ["Phase margin improvement","PD/PID adds phase lead near gain crossover → improves PM\nChoose crossover ωc where phase is maximally positive\nRule: place PID zero pair near ωc for maximum benefit"],
        ["Gain crossover design","Set Kp so |C(jωc)G(jωc)| = 1 at desired ωc\nωc sets closed-loop bandwidth and approximate speed\nHigher ωc → faster response but needs higher gain"],
        ["Integral-derivative interaction","Ti and Td must be chosen so integral doesn't erode derivative's PM benefit\nRule: Ti ≥ 4Td to ensure integral zero doesn't move too close to derivative zero\nISA standard: Td = Ti/4 as a starting guideline"],
        ["Loop shaping with PID","PID provides 3 degrees of freedom for loop shaping\nMore complex plants may need additional lead/lag compensators\nLead compensator: C_lead(s) = (s+z)/(s+p), p>z → phase lead"],
      ]}/>

      <h2 style={S.h2}>Anti-Windup</h2>
      <p style={S.p}>
        Every real actuator has limits — a valve can only open from 0% to 100%, a motor has a torque limit, a heater cannot output negative power. When the controller demands a signal outside these limits, the actuator saturates and the actual control action is less than demanded. If nothing is done, the integral term keeps accumulating, producing a very large internal value that must "unwind" before normal control resumes. This causes a large, unnecessary overshoot — integrator windup.
      </p>
      <DiagramAntiWindup/>
      <Table2 rows={[
        ["Windup mechanism","Saturation: u_actual = sat(u_controller, u_min, u_max)\nWhen saturated: e(t)>0 but u doesn't decrease y → error stays large\nIntegral keeps growing: ∫e·dt → large positive value\nWhen r decreases: huge integral → massive overshoot before unwinding"],
        ["Clamping (simplest)","if u_unlimited > u_max: integral_state += 0  (stop integrating)\nif u_unlimited < u_min: integral_state += 0\nSimple to implement; may leave small residual issue\nCondition: stop integrating when saturated AND error has same sign"],
        ["Back-calculation method","di/dt = e/Ti + (u_sat − u_raw) / Tt\nTt = tracking time constant (tune: Tt ≈ √(Ti·Td) for PID)\nSmoothly unwinds the integrator proportional to saturation excess\nInternational standard (IEC 61511); most industrial implementations"],
        ["Conditional integration","Only integrate when |e(t)| < ε (near setpoint)\nSimple; prevents windup on large setpoint changes\nMay slow down integral action even when not saturated"],
        ["Tracking time constant Tt","Tt determines how quickly integrator unwinds when saturated\nSmall Tt: fast unwinding but may cause oscillation near saturation\nLarge Tt: slow unwinding but stable\nOptimal: Tt = √(Ti·Td) for PID with derivative"],
        ["Windup in digital PID","Discrete integrator: I[k] = I[k-1] + Ki·e[k]·dt\nClamping: if u > u_max, I[k] = I[k-1] (don't update)\nBack-calculation: I[k] = I[k-1] + (Ki·e[k] + (u_sat−u)/Tt)·dt"],
        ["Practical importance","Actuator saturation is common: startup, large setpoint changes, disturbances\nWithout anti-windup: large overshoot → may trip safety interlocks\nAll industrial PID implementations include anti-windup"],
        ["Setpoint ramp rate limiting","Complementary strategy: limit how fast r(t) can change\nReduces chance of saturation in the first place\nRamp rate = max(dr/dt) chosen based on actuator limits"],
      ]}/>

      <h2 style={S.h2}>Derivative Filtering &amp; Practical PID</h2>
      <p style={S.p}>
        The ideal derivative Kd·s has unbounded gain at high frequencies, making it impossible to implement directly on any real signal that contains measurement noise. The solution is to add a first-order low-pass filter to the derivative, limiting its maximum gain to Kd·Nd. Alternatively, computing the derivative of the measured output y(t) rather than the error e(t) avoids the "derivative kick" — the impulsive control action that occurs when a step setpoint change is applied.
      </p>
      <DiagramDerivativeFilter/>
      <Table2 rows={[
        ["Ideal vs filtered derivative","Ideal: C_D(s) = Kd·s  →  |Kd·jω| → ∞ as ω→∞\nFiltered: C_Df(s) = Kd·Nd·s/(s+Nd)\nHigh-ω limit: |C_Df(jω)| → Kd·Nd (bounded)\nAdds pole at s=−Nd to limit high-frequency gain"],
        ["Filter coefficient N","N = pole location of derivative filter = bandwidth limit\nKd·Nd = maximum derivative gain  |  ωfilter = Nd rad/s\nTypical range: N = 5 to 20\nSmall N: heavy filtering but reduced derivative effectiveness"],
        ["Derivative kick","Sudden step in r(t) → sudden step in e(t) → impulse in de/dt\nWith ideal D: u(t) has impulse at step → actuator shock\nDerivative on measurement: u_D = −Kd·dy/dt\ny is smooth → no kick even for step reference change"],
        ["Derivative on measurement","u(t) = Kp·e(t) + Ki∫e − Kd·dy/dt\nStep reference: y initially unchanged → D term = 0 at step\nDisturbance: e changes → D term responds normally\nStandard in industrial PID (IEC 62381)"],
        ["Setpoint weighting for P","u = Kp(b·r − y) + Ki∫e − Kd·dy/dt\nb ∈ [0,1]: b=1 full P on error, b=0 P only on output\nb=0: no overshoot on reference step; b=1: fastest tracking\nc ∈ [0,1] similarly for D term (usually c=0)"],
        ["Noise bandwidth","System bandwidth ωBW must be well below noise frequency\nRule: N < ωBW / (10 × derivative gain ratio)\nAkso: if noise power is σ², D term noise ≈ σ·Kd·Nd\nChoose N to keep noise contribution below acceptable level"],
        ["Digital implementation (Euler)","Filtered D in z-domain: D[k] = D[k-1]·(1-Nd·dt) + Nd·Kd·(e[k]-e[k-1])\nOr derivative on measurement: D[k] = D[k-1]·(1-Nd·dt) + Nd·Kd·(y[k]-y[k-1])·(-1)\nSample time: dt ≤ 1/(5·Nd) for accurate derivative"],
        ["Complete practical PID","u[k] = Kp·(b·r[k]−y[k]) + I[k] + D[k]\nI[k] = I[k-1] + Ki·dt·e[k] + (u_sat−u_unsat)/Tt  (anti-windup)\nD[k] = (1−Nd·dt)·D[k-1] − Kd·Nd·(y[k]−y[k-1])  (D-o-M, filtered)\nu[k] = sat(Kp·(b·r−y)+I+D, u_min, u_max)"],
      ]}/>

      <h2 style={S.h2}>Tuning Method Comparison &amp; Practical Guidelines</h2>
      <p style={S.p}>
        No single tuning method is universally best. The choice depends on how well the plant is known, whether it can be taken offline for step tests, whether sustained oscillation is safe, and how tight the performance specification is. Modern approaches (IMC, SIMC) offer a single tuning parameter with a clear stability guarantee, which makes them preferable to Ziegler-Nichols for most applications.
      </p>
      <DiagramTuningComparison/>
      <Table2 rows={[
        ["Manual (iterative) tuning","Pros: no model needed; direct feel for plant\nCons: time-consuming; skill-dependent\nBest for: slow plants, low-volume production, experienced engineers"],
        ["ZN step (open-loop)","Pros: simple; only needs open-loop step response\nCons: aggressive (~25% OS); FOPDT approximation may be poor\nBest for: initial commissioning, quick startup"],
        ["ZN ultimate gain","Pros: directly uses closed-loop dynamics\nCons: requires sustained oscillation — may be unsafe\nBest for: bench tuning, simulation, when open-loop test is impractical"],
        ["IMC-based PID","C(s)=[(T+λL/2)/KTd·s²+...]; single tuning parameter λ\nPros: λ has clear meaning (closed-loop TC); smooth robust adjustment\nBest for: well-characterized plants; online retuning"],
        ["SIMC (Skogestad)","Simplified IMC: Kp=T/(K(λ+L)), Ti=min(T,4(λ+L)), Td=L/2\nλ = T/5 (aggressive) or 5L (robust)\nPros: simple formulas; good performance across wide range of plants\nBest for: practical process control; replaces ZN as first choice"],
        ["Pole placement / root locus","Design for specific closed-loop pole locations\nPros: directly meets transient specs (%OS, Ts)\nCons: needs plant TF; solutions may require impractical gains"],
        ["Auto-tuning (relay method)","Use relay (bang-bang) to automatically find Ku, Tu\nAsström & Hägglund (1984) — standard in modern controllers\nSafe: relay prevents large oscillations; automatic on DCS/PLC\nPros: no manual step test needed; works online"],
        ["Loop performance monitoring","IAE = ∫|e(t)|dt, ISE = ∫e²(t)dt, ITAE = ∫t|e(t)|dt\nITAE weights late errors more — good for optimising settling\nOnline: compute these metrics to detect detuning or plant change"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Time domain: u(t) = Kp·e + Ki·∫e·dt + Kd·de/dt  |  e(t) = r(t) − y(t)",
        "Laplace: C(s) = Kp + Ki/s + Kd·s  |  ISA form: Kp[1 + 1/(Ti·s) + Td·s]",
        "ZN open-loop PID: Kp=1.2T/(KL), Ti=2L, Td=0.5L  (from FOPDT K, L, T)",
        "ZN ultimate PID: Kp=0.6Ku, Ti=Tu/2, Td=Tu/8  (from Ku, Tu)",
        "SIMC: Kp=T/(K(λ+L)), Ti=min(T,4(λ+L)), Td=L/2  (λ=tuning parameter)",
        "Filtered derivative: C_Df(s) = Kd·N·s/(s+N)  — limits gain to Kd·N at high ω",
        "Anti-windup (back-calc): dI/dt = e/Ti + (u_sat−u)/Tt  |  Tt ≈ √(Ti·Td)",
        "Derivative on measurement: u_D = −Kd·dy/dt  (no derivative kick on reference steps)",
      ]}/>
    </div>
  );
}